import { useEffect, useState } from "react";
import { useNewerBuildAvailable } from "@/lib/appVersion";
import { usePendingSavesSummary } from "@/lib/offline/pendingSaves";

/*
 * Words, no motion (DESIGN.md §10). Highlight Blue is the current-state accent, as on the
 * connection indicator. Sits under modal dialogs (--z-modal 300) so it never covers their buttons,
 * and "Later" moves it out of the way for a while: it is fixed at the bottom, over the bottom of
 * the testing screens (posture photo feet, wizard controls).
 */
const BUTTON =
  "min-h-[44px] shrink-0 border px-4 font-[var(--font-display)] text-xs font-bold uppercase tracking-[0.08em] chamfer-sm";
const PRIMARY = `${BUTTON} border-[var(--color-highlight-blue)] bg-[var(--color-highlight-blue)] text-[var(--color-navy)]`;
const SECONDARY = `${BUTTON} border-[var(--color-border)] text-[var(--color-white)]`;
const LATER_MS = 10 * 60_000;

export function AppUpdateNotice() {
  const newer = useNewerBuildAvailable();
  const [hiddenUntil, setHiddenUntil] = useState(0);

  useEffect(() => {
    if (!hiddenUntil) return;
    const t = window.setTimeout(() => setHiddenUntil(0), Math.max(0, hiddenUntil - Date.now()));
    return () => window.clearTimeout(t);
  }, [hiddenUntil]);

  if (!newer || hiddenUntil) return null;
  // Mounted only once a newer build is out, so other pages never start the save queue for it.
  return <UpdateBar onLater={() => setHiddenUntil(Date.now() + LATER_MS)} />;
}

function UpdateBar({ onLater }: { onLater: () => void }) {
  // Only clips held in memory are lost by a reload; stored saves resend after it.
  const { inMemoryUnsaved: clips } = usePendingSavesSummary();
  const clipsWord = `${clips} ${clips === 1 ? "video" : "videos"}`;

  const reload = () => {
    if (
      clips > 0 &&
      !window.confirm(`${clipsWord} haven't finished uploading and will need to be recorded again. Reload anyway?`)
    ) {
      return;
    }
    window.location.reload();
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[250] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div
        role="alert"
        className="pointer-events-auto flex w-full max-w-xl flex-wrap items-center justify-between gap-3 border border-[var(--color-highlight-blue)] bg-[var(--color-navy)] px-4 py-3 text-sm text-[var(--color-white)] chamfer-sm"
      >
        <p className="min-w-0 flex-1 leading-snug">
          {clips > 0
            ? `A newer version of Fit2Lift is out. Reload once ${clipsWord} ${clips === 1 ? "finishes" : "finish"} uploading.`
            : "A newer version of Fit2Lift is out. Reload to use it."}
        </p>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={onLater} className={SECONDARY}>
            Later
          </button>
          <button type="button" onClick={reload} className={clips > 0 ? SECONDARY : PRIMARY}>
            {clips > 0 ? "Reload anyway" : "Reload"}
          </button>
        </div>
      </div>
    </div>
  );
}
